<template>
  <Transition name="panel-slide">
    <div class="category-manager-panel" v-if="visible">
      <v-card
        class="shadcard"
        max-width="420"
        style="height: 100%; display: flex; flex-direction: column"
      >
        <!-- Header -->
        <div
          class="d-flex align-center justify-space-between px-4 py-3"
          style="border-bottom: 1px solid hsl(214.3 31.8% 91.4%)"
        >
          <div>
            <p
              style="
                font-size: 9pt;
                text-transform: uppercase;
                letter-spacing: 0.08em;
                color: #71717a;
                margin-bottom: 4px;
              "
            >
              Category
            </p>
            <h2
              v-if="localCategory"
              style="font-size: 14pt; font-weight: 600; letter-spacing: -0.02em"
            >
              {{ localCategory.name }}
            </h2>
            <p v-else style="font-size: 10pt; color: #71717a">
              Select a category to manage
            </p>
          </div>
          <div class="d-flex align-center" style="gap: 8px">
            <v-chip
              v-if="localCategory"
              size="x-small"
              label
              :color="statusChip.color"
              variant="flat"
              style="font-size: 9pt"
            >
              {{ statusChip.label }}
            </v-chip>
            <!-- <NewCategory type="plus" /> -->
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              @click="$emit('close')"
            />
          </div>
        </div>

        <v-divider />

        <div
          class="px-4 py-3"
          v-if="localCategory"
          style="flex: 1; overflow-y: auto"
        >
          <!-- Active toggle & schedule summary -->
          <div class="mb-4">
            <div
              class="d-flex align-center justify-space-between mb-2"
              style="gap: 10px"
            >
              <div>
                <p
                  style="
                    font-size: 10pt;
                    font-weight: 500;
                    margin-bottom: 2px;
                    color: #09090b;
                  "
                >
                  Schedule
                </p>
                <p style="font-size: 9pt; color: #71717a">
                  When Stix should post for this category.
                </p>
              </div>
              <v-switch
                v-model="active"
                hide-details
                density="compact"
                color="success"
                inset
              >
                <template #label>
                  <span style="font-size: 9pt">
                    {{ active ? "Active" : "Paused" }}
                  </span>
                </template>
              </v-switch>
            </div>
            <div
              style="
                padding: 8px 10px;
                border-radius: 6px;
                background: #f9fafb;
                border: 1px dashed hsl(214.3 31.8% 91.4%);
                font-size: 10pt;
                color: #4b5563;
              "
            >
              <ScheduleDialog
                v-if="localCategory"
                :key="localCategory.uid"
                :category="localCategory"
              />
            </div>
          </div>

          <v-divider class="my-3" />

          <!-- Description -->
          <div class="mb-4">
            <p
              style="
                font-size: 10pt;
                font-weight: 500;
                margin-bottom: 4px;
                color: #09090b;
              "
            >
              Description
            </p>
            <p style="font-size: 9pt; color: #71717a; margin-bottom: 6px">
              What this category is about and what kind of content belongs here.
            </p>
            <v-textarea
              v-model="localCategory.description"
              rows="3"
              auto-grow
              density="comfortable"
              variant="outlined"
              hide-details
              class="shadcn-input panel-input-sm"
            />
          </div>

          <!-- Goals -->
          <div class="mb-4">
            <p
              style="
                font-size: 10pt;
                font-weight: 500;
                margin-bottom: 4px;
                color: #09090b;
              "
            >
              Goals
            </p>
            <p style="font-size: 9pt; color: #71717a; margin-bottom: 6px">
              What should this category help you achieve?
            </p>

            <div
              v-if="localCategory.goals && localCategory.goals.length > 0"
              class="mb-2 d-flex flex-wrap"
              style="gap: 6px"
            >
              <v-chip
                v-for="(goal, index) in localCategory.goals"
                :key="index"
                closable
                size="small"
                variant="outlined"
                style="font-size: 9pt"
                @click:close="removeGoal(index)"
              >
                {{ goal }}
              </v-chip>
            </div>

            <v-text-field
              v-model="newGoal"
              placeholder="Inform audience about ..."
              density="compact"
              variant="outlined"
              hide-details
              class="shadcn-input panel-input-sm"
              @keyup.enter="addGoal"
            >
              <template #append-inner>
                <v-btn
                  icon="mdi-plus"
                  size="x-small"
                  variant="text"
                  @click="addGoal"
                />
              </template>
            </v-text-field>
          </div>

          <!-- Keywords (optional but useful for AI) -->
          <div class="mb-4">
            <p
              style="
                font-size: 10pt;
                font-weight: 500;
                margin-bottom: 4px;
                color: #09090b;
              "
            >
              Keywords
            </p>
            <p style="font-size: 9pt; color: #71717a; margin-bottom: 6px">
              Short phrases that describe topics this category should cover.
            </p>

            <div
              v-if="localCategory.keywords && localCategory.keywords.length > 0"
              class="mb-2 d-flex flex-wrap"
              style="gap: 6px"
            >
              <v-chip
                v-for="(keyword, index) in localCategory.keywords"
                :key="index"
                closable
                size="small"
                variant="outlined"
                style="font-size: 9pt"
                @click:close="removeKeyword(index)"
              >
                {{ keyword }}
              </v-chip>
            </div>

            <v-text-field
              v-model="newKeyword"
              placeholder="e.g. founder stories"
              density="compact"
              variant="outlined"
              hide-details
              class="shadcn-input panel-input-sm"
              @keyup.enter="addKeyword"
            >
              <template #append-inner>
                <v-btn
                  icon="mdi-plus"
                  size="x-small"
                  variant="text"
                  @click="addKeyword"
                />
              </template>
            </v-text-field>
          </div>
        </div>

        <v-divider />

        <!-- Footer -->
        <div
          class="px-4 py-3 d-flex justify-end align-center"
          style="border-top: 1px solid hsl(214.3 31.8% 91.4%)"
        >
          <v-btn
            v-if="localCategory"
            size="small"
            variant="text"
            color="error"
            class="text-none font-weight-medium"
            @click="deleteCurrentCategory"
          >
            Delete
          </v-btn>
        </div>
      </v-card>
    </div>
  </Transition>
</template>

<script>
import { mapState } from "vuex";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/firebase";
import ScheduleDialog from "@/components/scheduler/ScheduleDialog.vue";
// import NewCategory from "@/components/NewCategory.vue";

export default {
  name: "CategoryManagerPanel",
  components: {
    ScheduleDialog,
    // NewCategory,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    selectedCategory: {
      type: Object,
      default: null,
    },
    categories: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:modelValue", "close", "saved", "categoryChanged"],
  data() {
    return {
      localCategory: null,
      newGoal: "",
      newKeyword: "",
      isLoadingCategory: false,
    };
  },
  computed: {
    ...mapState({
      userProfile: (state) => state.users.userProfile,
    }),
    visible: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit("update:modelValue", val);
        if (!val) {
          this.$emit("close");
        }
      },
    },
    statusChip() {
      if (!this.localCategory) {
        return { label: "", color: "default" };
      }
      const status = this.localCategory.status || "draft";
      if (status === "suggested") {
        return {
          label: "AI suggested",
          color: "info",
        };
      }
      if (this.active) {
        return { label: "Active", color: "success" };
      }
      return { label: "Paused", color: "grey" };
    },
    active: {
      get() {
        return this.localCategory?.active ?? false;
      },
      async set(value) {
        if (!this.localCategory || !this.userProfile?.workspace) return;
        this.localCategory.active = value;
        await updateDoc(
          doc(
            db,
            "workspaces",
            this.userProfile.workspace,
            "categories",
            this.localCategory.uid,
          ),
          { active: value },
        );
        this.$store.dispatch("fetchCategories");
      },
    },
  },
  watch: {
    selectedCategory: {
      immediate: true,
      handler(newVal) {
        if (
          newVal &&
          this.localCategory &&
          newVal.uid === this.localCategory.uid
        ) {
          return;
        }
        this.isLoadingCategory = true;
        if (this._autoSaveTimer) clearTimeout(this._autoSaveTimer);
        if (newVal) {
          this.localCategory = JSON.parse(JSON.stringify(newVal));
          this.$emit("categoryChanged", this.localCategory);
        } else {
          this.localCategory = null;
        }
        this.$nextTick(() => {
          this.isLoadingCategory = false;
        });
      },
    },
    modelValue(val) {
      if (!val) {
        this.resetLocal();
      }
    },
    "localCategory.description"() {
      if (!this.isLoadingCategory) this.debouncedAutoSave();
    },
    "localCategory.goals": {
      handler() {
        if (!this.isLoadingCategory) this.debouncedAutoSave();
      },
      deep: true,
    },
    "localCategory.keywords": {
      handler() {
        if (!this.isLoadingCategory) this.debouncedAutoSave();
      },
      deep: true,
    },
  },
  beforeUnmount() {
    if (this._autoSaveTimer) clearTimeout(this._autoSaveTimer);
  },
  methods: {
    debouncedAutoSave() {
      if (this._autoSaveTimer) clearTimeout(this._autoSaveTimer);
      this._autoSaveTimer = setTimeout(() => {
        this.autoSave();
      }, 1000);
    },
    async autoSave() {
      if (!this.localCategory || !this.userProfile?.workspace) return;
      await updateDoc(
        doc(
          db,
          "workspaces",
          this.userProfile.workspace,
          "categories",
          this.localCategory.uid,
        ),
        {
          description: this.localCategory.description || "",
          goals: this.localCategory.goals || [],
          keywords: this.localCategory.keywords || [],
        },
      );
    },
    addGoal() {
      const value = (this.newGoal || "").trim();
      if (!value) return;
      if (!Array.isArray(this.localCategory.goals)) {
        this.localCategory.goals = [];
      }
      this.localCategory.goals.push(value);
      this.newGoal = "";
    },
    removeGoal(index) {
      if (!Array.isArray(this.localCategory.goals)) return;
      this.localCategory.goals.splice(index, 1);
    },
    addKeyword() {
      const value = (this.newKeyword || "").trim();
      if (!value) return;
      if (!Array.isArray(this.localCategory.keywords)) {
        this.localCategory.keywords = [];
      }
      this.localCategory.keywords.push(value);
      this.newKeyword = "";
    },
    removeKeyword(index) {
      if (!Array.isArray(this.localCategory.keywords)) return;
      this.localCategory.keywords.splice(index, 1);
    },
    async deleteCurrentCategory() {
      if (!this.localCategory) return;
      await this.$store.dispatch("deleteCategory", {
        uid: this.localCategory.uid,
        silent: false,
      });
      this.visible = false;
    },
    resetLocal() {
      this.isLoadingCategory = true;
      if (this.selectedCategory) {
        this.localCategory = JSON.parse(JSON.stringify(this.selectedCategory));
        this.newGoal = "";
        this.newKeyword = "";
      }
      this.$nextTick(() => {
        this.isLoadingCategory = false;
      });
    },
  },
};
</script>

<style scoped>
.category-manager-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 95;
  padding: 16px;
  display: flex;
  justify-content: flex-end;
  pointer-events: none;
}

.category-manager-panel > * {
  pointer-events: auto;
}

.shadcard {
  border: 1px solid hsl(214.3 31.8% 91.4%);
  border-radius: 0.5rem;
  box-shadow: 0 10px 40px rgba(15, 23, 42, 0.35);
  background: #ffffff;
}

.panel-slide-enter-active,
.panel-slide-leave-active {
  transition:
    transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.25s ease;
}

.panel-slide-enter-from,
.panel-slide-leave-to {
  transform: translateX(30px);
  opacity: 0;
}

.panel-input-sm :deep(textarea),
.panel-input-sm :deep(input) {
  font-size: 9pt !important;
}
</style>
